<template lang="pug">
div
    v-card.mb-4
        v-card-title Campaign Analytics
            v-spacer
            v-select(
                v-model="selectedCampaignId"
                :items="campaignOptions"
                item-text="name"
                item-value="id"
                label="Select Campaign"
                outlined
                dense
                hide-details
                style="max-width: 400px;"
            )
            v-btn.ml-2(icon @click="refreshAnalytics" :loading="isRefreshing" title="Refresh Analytics")
                v-icon mdi-refresh

    v-card(v-if="selectedCampaignId")
        v-card-text
            v-alert(v-if="error" type="error" dismissible @input="error = null") {{ error }}

            div(v-if="isLoading" style="text-align: center; padding: 40px;")
                v-progress-circular(indeterminate color="primary" size="64")
                p.mt-4 Loading analytics...

            div(v-else-if="analytics")
                v-row
                    v-col(cols="12" md="3")
                        v-card.text-center(elevation="2")
                            v-card-text
                                div.text-h6.primary--text Total Ratings
                                div.text-h4.mt-2 {{ analytics.total_ratings }}
                                v-icon.mt-2(large color="primary") mdi-star

                    v-col(cols="12" md="3")
                        v-card.text-center(elevation="2")
                            v-card-text
                                div.text-h6.success--text Tokens Distributed
                                div.text-h4.mt-2 {{ analytics.tokens_distributed }} 🪙
                                v-icon.mt-2(large color="success") mdi-coin

                    v-col(cols="12" md="3")
                        v-card.text-center(elevation="2")
                            v-card-text
                                div.text-h6.info--text Average Rating
                                div.text-h4.mt-2 {{ analytics.average_rating.toFixed(2) }}
                                v-icon.mt-2(large color="info") mdi-chart-line

                    v-col(cols="12" md="3")
                        v-card.text-center(elevation="2")
                            v-card-text
                                div.text-h6.warning--text Completion Rate
                                div.text-h4.mt-2 {{ analytics.completion_rate.toFixed(1) }}%
                                v-progress-circular.mt-2(
                                    :value="analytics.completion_rate"
                                    :color="getCompletionColor(analytics.completion_rate)"
                                    size="50"
                                    width="5"
                                ) {{ Math.round(analytics.completion_rate) }}%

                v-row.mt-4
                    v-col(cols="12")
                        v-card
                            v-card-title
                                | Participants
                                v-spacer
                                v-chip.mr-2(color="blue" text-color="white" small) Total: {{ analytics.total_participants || allParticipants.length }}
                                v-chip(color="green" text-color="white" small) Completed: {{ analytics.total_completed || completedParticipants.length }}
                            v-card-text
                                v-tabs(v-model="participantTab")
                                    v-tab All Participants
                                    v-tab Completed
                                v-tabs-items(v-model="participantTab")
                                    v-tab-item
                                        v-data-table(
                                            :headers="participantHeaders"
                                            :items="allParticipants"
                                            :items-per-page="10"
                                            class="elevation-0"
                                            :no-data-text="'No participants found for this campaign'"
                                        )
                                            template(v-slot:item.name="{ item }")
                                                | {{ item.fname }} {{ item.lname }}
                                            template(v-slot:item.source="{ item }")
                                                v-chip(:color="item.source === 'GROUP' ? 'blue' : 'purple'" text-color="white" small) {{ item.source }}
                                            template(v-slot:item.group_name="{ item }")
                                                | {{ item.group_name || '—' }}
                                    v-tab-item
                                        v-data-table(
                                            :headers="completedHeaders"
                                            :items="completedParticipants"
                                            :items-per-page="10"
                                            class="elevation-0"
                                            :no-data-text="'No completions found for this campaign'"
                                        )
                                            template(v-slot:item.name="{ item }")
                                                | {{ item.fname }} {{ item.lname }}
                                            template(v-slot:item.rating="{ item }")
                                                v-chip(:color="getRatingColor(item.rating)" text-color="white" small) {{ item.rating !== null ? item.rating : '—' }}
                                            template(v-slot:item.completed_at="{ item }")
                                                | {{ item.completed_at | dateFormat }}

                v-row.mt-4
                    v-col(cols="12")
                        v-card
                            v-card-title Campaign Details
                            v-card-text
                                v-simple-table
                                    tbody
                                        tr
                                            td.font-weight-bold Campaign Name
                                            td {{ analytics.campaign_name || `Campaign #${analytics.campaign_id}` }}
                                        tr
                                            td.font-weight-bold Campaign ID
                                            td {{ analytics.campaign_id }}
                                        tr
                                            td.font-weight-bold Cache Status
                                            td
                                                v-chip(small :color="analytics.is_cached ? 'blue' : 'orange'" text-color="white") 
                                                    | {{ analytics.is_cached ? 'Cached' : 'Fresh' }}
                                        tr
                                            td.font-weight-bold Last Updated
                                            td {{ analytics.cached_at | dateFormat }}

                v-row.mt-4
                    v-col(cols="12")
                        v-card
                            v-card-title Actions
                            v-card-text
                                v-btn.mr-2(color="primary" @click="exportToCSV" :loading="isExporting")
                                    v-icon(left) mdi-file-export
                                    | Export to CSV
                                v-btn.mr-2(color="green" @click="exportToExcel" :loading="isExporting")
                                    v-icon(left) mdi-file-excel
                                    | Export to Excel
                                v-btn(color="orange" @click="invalidateCache" :loading="isInvalidating")
                                    v-icon(left) mdi-cached
                                    | Invalidate Cache

    v-card(v-else)
        v-card-text.text-center
            v-icon(size="80" color="grey lighten-1") mdi-chart-box-outline
            p.mt-4.text-h6.grey--text Select a campaign to view analytics
</template>

<script>
import feathersClient from '@/plugins/feathers-client';

export default {
    name: 'CampaignAnalytics',
    data() {
        return {
            selectedCampaignId: null,
            campaigns: [],
            analytics: null,
            isLoading: false,
            isRefreshing: false,
            isExporting: false,
            isInvalidating: false,
            error: null,
            participantTab: 0,
            participantHeaders: [
                { text: 'User ID', value: 'user_id', width: '90px' },
                { text: 'Name', value: 'name' },
                { text: 'Email', value: 'email' },
                { text: 'Type', value: 'source', width: '110px' },
                { text: 'Group', value: 'group_name' }
            ],
            completedHeaders: [
                { text: 'User ID', value: 'user_id', width: '90px' },
                { text: 'Name', value: 'name' },
                { text: 'Email', value: 'email' },
                { text: 'Rating', value: 'rating', width: '100px' },
                { text: 'Completed At', value: 'completed_at' }
            ]
        }
    },
    computed: {
        campaignOptions() {
            return this.campaigns.map(c => ({
                id: c.id,
                name: c.campaign_name && c.campaign_name.trim() 
                    ? c.campaign_name 
                    : `Campaign #${c.id}`
            }))
        },
        allParticipants() {
            return (this.analytics && this.analytics.all_participants) || []
        },
        completedParticipants() {
            return (this.analytics && this.analytics.completed_participants) || []
        }
    },
    async mounted() {
        // Fetch campaigns list
        try {
            const response = await feathersClient.service('bh-token-campaigns').find({
                query: {
                    $limit: 100,
                    $sort: { date_created: -1 }
                }
            })
            this.campaigns = response.data || response
        } catch (err) {
            console.error('Error loading campaigns:', err)
            this.error = 'Failed to load campaigns list'
        }
    },
    watch: {
        selectedCampaignId(newId) {
            if (newId) {
                this.fetchAnalytics()
            }
        }
    },
    methods: {
        async fetchAnalytics() {
            if (!this.selectedCampaignId) return

            this.isLoading = true
            this.error = null

            try {
                const response = await feathersClient.service('bh-campaign-analytics').get(this.selectedCampaignId)
                this.analytics = response
            } catch (err) {
                console.error('Error fetching analytics:', err)
                this.error = err.message || 'Failed to fetch analytics'
            } finally {
                this.isLoading = false
            }
        },
        async refreshAnalytics() {
            if (!this.selectedCampaignId) return

            this.isRefreshing = true
            this.error = null

            try {
                await this.invalidateCache()
                await this.fetchAnalytics()
            } catch (err) {
                console.error('Error refreshing analytics:', err)
                this.error = err.message || 'Failed to refresh analytics'
            } finally {
                this.isRefreshing = false
            }
        },
        async invalidateCache() {
            if (!this.selectedCampaignId) return

            this.isInvalidating = true
            this.error = null

            try {
                await feathersClient.service('bh-campaign-analytics').patch(this.selectedCampaignId, {})
                await this.fetchAnalytics()
                this.$store.commit('addAlert', { 
                    message: 'Cache invalidated successfully', 
                    type: 'success' 
                })
            } catch (err) {
                console.error('Error invalidating cache:', err)
                this.error = err.message || 'Failed to invalidate cache'
            } finally {
                this.isInvalidating = false
            }
        },
        exportToCSV() {
            if (!this.analytics) return

            this.isExporting = true

            try {
                const headers = ['Metric', 'Value']
                const rows = [
                    ['Campaign ID', this.analytics.campaign_id],
                    ['Campaign Name', this.analytics.campaign_name || `Campaign #${this.analytics.campaign_id}`],
                    ['Total Ratings', this.analytics.total_ratings],
                    ['Tokens Distributed', this.analytics.tokens_distributed],
                    ['Average Rating', this.analytics.average_rating.toFixed(2)],
                    ['Completion Rate (%)', this.analytics.completion_rate.toFixed(1)],
                    ['Cached At', this.$options.filters.dateFormat(this.analytics.cached_at)]
                ]

                const csvContent = [
                    headers.join(','),
                    ...rows.map(row => row.join(','))
                ].join('\n')

                const blob = new Blob([csvContent], { type: 'text/csv' })
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `campaign_${this.analytics.campaign_id}_analytics.csv`
                a.click()
                window.URL.revokeObjectURL(url)

                this.$store.commit('addAlert', { 
                    message: 'Analytics exported to CSV successfully', 
                    type: 'success' 
                })
            } catch (err) {
                console.error('Error exporting to CSV:', err)
                this.error = 'Failed to export to CSV'
            } finally {
                this.isExporting = false
            }
        },
        exportToExcel() {
            // For Excel export, we use the same CSV logic since Excel can open CSV files
            // In a production environment, you might want to use a library like xlsx
            this.exportToCSV()
        },
        getCompletionColor(rate) {
            if (rate >= 80) return 'success'
            if (rate >= 50) return 'warning'
            return 'error'
        },
        getRatingColor(rating) {
            if (rating === null || rating === undefined) return 'grey'
            if (rating >= 4) return 'success'
            if (rating >= 3) return 'warning'
            return 'error'
        }
    },
    filters: {
        dateFormat(value) {
            if (!value) return 'N/A'
            const date = new Date(value)
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        }
    }
}
</script>

<style scoped>
</style>
